/**
 * Drivers (řidiči) + identity documents — `/v1/customers/{id}/ridici`.
 *
 * Rental-service persons attached to a customer (migration 0026, see
 * .agents/pujcovna-plan.md §0). One query key per customer; every
 * mutation invalidates it, so the section re-renders from the server shape.
 * Scan uploads go multipart via `fetch` + the bearer, like the attachment
 * mutations in kontakty/api.ts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { getAuthHeaders } from '@/lib/auth';

export type DokladTyp = 'op' | 'rp' | 'pas';
export type DokladStrana = 'predni' | 'zadni';

export interface Doklad {
  id_dokladu: number;
  id_ridice: number;
  typ: DokladTyp;
  cislo: string;
  datum_vydani: string | null;
  datum_platnosti: string | null;
  ma_predni: boolean;
  ma_zadni: boolean;
  vytvoreno: string;
}

export interface Ridic {
  id_ridice: number;
  id_zakaznika: number;
  jmeno: string;
  prijmeni: string;
  datum_narozeni: string | null;
  telefon: string;
  email: string;
  poznamka: string | null;
  vytvoreno: string;
  doklady: Doklad[];
}

export interface RidicBody {
  jmeno: string;
  prijmeni: string;
  datum_narozeni: string | null;
  telefon: string;
  email: string;
  poznamka: string | null;
}

export interface DokladBody {
  typ: DokladTyp;
  cislo: string;
  datum_vydani: string | null;
  datum_platnosti: string | null;
}

const ridiciKey = (customerId: number) => ['customers', 'ridici', customerId] as const;
const ridiciBase = (customerId: number) => `/v1/customers/${customerId}/ridici`;

export function dokladSidePath(
  customerId: number,
  ridicId: number,
  dokladId: number,
  strana: DokladStrana,
): string {
  return `${ridiciBase(customerId)}/${ridicId}/doklady/${dokladId}/soubor/${strana}`;
}

/**
 * `fresh`: a consumer that must not act on a cached copy (the rental handover dialog picks drivers and judges
 * their documents) — never stale, and re-read on every mount, so a driver added in another tab shows up.
 */
export function useRidiciQuery(customerId: number | null, opts: { fresh?: boolean } = {}) {
  return useQuery({
    queryKey: ridiciKey(customerId ?? 0),
    enabled: customerId != null,
    ...(opts.fresh ? { staleTime: 0, refetchOnMount: 'always' as const } : {}),
    queryFn: () => api.get<{ ridici: Ridic[] }>(ridiciBase(customerId as number)),
    select: (data) => data.ridici,
  });
}

function useInvalidate(customerId: number) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ridiciKey(customerId) });
}

export function useCreateRidicMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: (body: RidicBody) =>
      api.post<{ status: 'ok'; id_ridice: number }>(ridiciBase(customerId), body),
    onSuccess: invalidate,
  });
}

export function useUpdateRidicMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: ({ ridicId, body }: { ridicId: number; body: RidicBody }) =>
      api.put<{ status: 'ok' }>(`${ridiciBase(customerId)}/${ridicId}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteRidicMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: (ridicId: number) =>
      api.delete<{ status: 'ok' }>(`${ridiciBase(customerId)}/${ridicId}`),
    onSuccess: invalidate,
  });
}

export function useCreateDokladMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: ({ ridicId, body }: { ridicId: number; body: DokladBody }) =>
      api.post<{ status: 'ok'; id_dokladu: number }>(
        `${ridiciBase(customerId)}/${ridicId}/doklady`,
        body,
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateDokladMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: ({ ridicId, dokladId, body }: { ridicId: number; dokladId: number; body: DokladBody }) =>
      api.put<{ status: 'ok' }>(`${ridiciBase(customerId)}/${ridicId}/doklady/${dokladId}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteDokladMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: ({ ridicId, dokladId }: { ridicId: number; dokladId: number }) =>
      api.delete<{ status: 'ok' }>(`${ridiciBase(customerId)}/${ridicId}/doklady/${dokladId}`),
    onSuccess: invalidate,
  });
}

export function useUploadDokladSideMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: async ({
      ridicId,
      dokladId,
      strana,
      file,
    }: {
      ridicId: number;
      dokladId: number;
      strana: DokladStrana;
      file: File;
    }) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/api${dokladSidePath(customerId, ridicId, dokladId, strana)}`, {
        method: 'PUT',
        body: fd,
        credentials: 'same-origin',
        headers: { Accept: 'application/json', ...getAuthHeaders() },
      });
      if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as { error?: string };
          if (body.error) message = body.error;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(message);
      }
      return (await res.json()) as { status: 'ok' };
    },
    onSuccess: invalidate,
  });
}

export function useDeleteDokladSideMutation(customerId: number) {
  const invalidate = useInvalidate(customerId);
  return useMutation({
    mutationFn: ({ ridicId, dokladId, strana }: { ridicId: number; dokladId: number; strana: DokladStrana }) =>
      api.delete<{ status: 'ok' }>(dokladSidePath(customerId, ridicId, dokladId, strana)),
    onSuccess: invalidate,
  });
}
